import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import storeThemeChat from '../../../store/storeThemeChat';
import { MuiInputColorControlled } from '../../Form/Input/inputColor';
import { MuiSelectControlled } from '../../Form/Select';
import { PreviewChatbot } from './PreviewChatbot';

const fontFamily = [
  'Roboto, sans-serif',
  'Inter, sans-serif',
  'Arial, sans-serif',
  'Arial Black, sans-serif',
  'Comic Sans MS, cursive',
  'Courier New, monospace',
  'Georgia, serif',
  'Impact, sans-serif',
  'Lucida Console, monospace',
  'Lucida Sans Unicode, sans-serif',
  'Palatino Linotype, serif',
  'Tahoma, sans-serif',
  'Times New Roman, serif',
  'Trebuchet MS, sans-serif',
  'Verdana, sans-serif', // Added missing fonts
  'Garamond, serif', // Added missing fonts
  'Brush Script MT, cursive', // Added missing fonts
];

const ThemeSchema = z.object({
  defaultTheme: z.nullable(z.string()),
  brandColor: z.nullable(z.string()),
  inputBorderColor: z.nullable(z.string()),
  font: z.nullable(z.string()),
  fontWeight: z.nullable(z.string()),
  pageBackground: z.nullable(z.string()),
  pageText: z.nullable(z.string()),
  responseContainer: z.nullable(z.string()),
  responseText: z.nullable(z.string()),
  userContainer: z.nullable(z.string()),
  userText: z.nullable(z.string()),
});

type ITheme = z.infer<typeof ThemeSchema>;

export function Theme({ chatData, onSetFlow }: any) {
  const formsMethods = useForm<ITheme>({
    resolver: zodResolver(ThemeSchema),
    defaultValues: {
      defaultTheme: chatData.defaultTheme,
    },
  });
  const theme = storeThemeChat();
  const [isEditDesign, setIsEditDesign] = useState(
    chatData.defaultTheme === 'my-theme' ? true : false,
  );
  const setNotification = useNotificationStore(state => state.setNotification);

  async function handleSubmitForm(data: ITheme) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        defaultTheme: isEditDesign ? 'my-theme' : data.defaultTheme,
        brandColor: data.brandColor,
        inputBorderColor: data.inputBorderColor,
        font: data.font,
        fontWeight: data.fontWeight,
        pageBackground: data.pageBackground,
        pageText: data.pageText,
        responseContainer: data.responseContainer,
        responseText: data.responseText,
        userContainer: data.userContainer,
        userText: data.userText,
      });

      onSetFlow(response.data);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error saving customize interface form',
      });
    }
  }

  function handleEditDesign() {
    setIsEditDesign(!isEditDesign);
  }

  function definesThemeProperties(defaultTheme: any) {
    switch (defaultTheme) {
      case 'black-light':
        theme.setDefaultTheme('black-light');
        theme.setBrandColor('#101828');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        formsMethods.setValue('defaultTheme', 'black-light');
        formsMethods.setValue('brandColor', '#101828');
        formsMethods.setValue('inputBorderColor', '#EAECF0');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#FFF');
        formsMethods.setValue('pageText', '#475467');
        formsMethods.setValue('responseContainer', '#F2F4F7');
        formsMethods.setValue('responseText', '#101828');
        formsMethods.setValue('userContainer', '#F2F4F7');
        formsMethods.setValue('userText', '#475467');
        break;

      case 'blue-light':
        theme.setDefaultTheme('blue-light');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        formsMethods.setValue('defaultTheme', 'blue-light');
        formsMethods.setValue('brandColor', '#3650DE');
        formsMethods.setValue('inputBorderColor', '#EAECF0');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#FFF');
        formsMethods.setValue('pageText', '#475467');
        formsMethods.setValue('responseContainer', '#F2F4F7');
        formsMethods.setValue('responseText', '#101828');
        formsMethods.setValue('userContainer', '#F2F4F7');
        formsMethods.setValue('userText', '#475467');
        break;

      case 'purple-light':
        theme.setDefaultTheme('purple-light');
        theme.setBrandColor('#7B4CFE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        formsMethods.setValue('defaultTheme', 'purple-light');
        formsMethods.setValue('brandColor', '#7B4CFE');
        formsMethods.setValue('inputBorderColor', '#EAECF0');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#FFF');
        formsMethods.setValue('pageText', '#475467');
        formsMethods.setValue('responseContainer', '#F2F4F7');
        formsMethods.setValue('responseText', '#101828');
        formsMethods.setValue('userContainer', '#F2F4F7');
        formsMethods.setValue('userText', '#475467');
        break;

      case 'orange-light':
        theme.setDefaultTheme('orange-light');
        theme.setBrandColor('#FF9103');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setPageBackground('#FFF');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        formsMethods.setValue('defaultTheme', 'orange-light');
        formsMethods.setValue('brandColor', '#FF9103');
        formsMethods.setValue('inputBorderColor', '#EAECF0');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#FFF');
        formsMethods.setValue('pageText', '#475467');
        formsMethods.setValue('responseContainer', '#F2F4F7');
        formsMethods.setValue('responseText', '#101828');
        formsMethods.setValue('userContainer', '#F2F4F7');
        formsMethods.setValue('userText', '#475467');
        break;

      case 'blue-dark':
        theme.setDefaultTheme('blue-dark');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');

        formsMethods.setValue('defaultTheme', 'blue-dark');
        formsMethods.setValue('brandColor', '#3650DE');
        formsMethods.setValue('inputBorderColor', '#344054');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#1D2939');
        formsMethods.setValue('pageText', '#D0D5DD');
        formsMethods.setValue('responseContainer', '#344054');
        formsMethods.setValue('responseText', '#FFFFFF');
        formsMethods.setValue('userContainer', '#101828');
        formsMethods.setValue('userText', '#D0D5DD');
        break;

      case 'orange-dark':
        theme.setDefaultTheme('orange-dark');
        theme.setBrandColor('#FF9103');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');

        formsMethods.setValue('defaultTheme', 'orange-dark');
        formsMethods.setValue('brandColor', '#FF9103');
        formsMethods.setValue('inputBorderColor', '#344054');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#1D2939');
        formsMethods.setValue('pageText', '#D0D5DD');
        formsMethods.setValue('responseContainer', '#344054');
        formsMethods.setValue('responseText', '#FFFFFF');
        formsMethods.setValue('userContainer', '#101828');
        formsMethods.setValue('userText', '#D0D5DD');
        break;

      case 'green-dark':
        theme.setDefaultTheme('green-dark');
        theme.setBrandColor('#4CAF50');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');

        formsMethods.setValue('defaultTheme', 'green-dark');
        formsMethods.setValue('brandColor', '#4CAF50');
        formsMethods.setValue('inputBorderColor', '#344054');
        formsMethods.setValue('font', 'Roboto, sans-serif');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#1D2939');
        formsMethods.setValue('pageText', '#D0D5DD');
        formsMethods.setValue('responseContainer', '#344054');
        formsMethods.setValue('responseText', '#FFFFFF');
        formsMethods.setValue('userContainer', '#101828');
        formsMethods.setValue('userText', '#D0D5DD');
        break;

      case 'my-theme':
        theme.setDefaultTheme('my-theme');
        theme.setBrandColor(chatData.brandColor);
        theme.setInputBorderColor(chatData.brandColor);
        theme.setFont(chatData.font);
        theme.setFontWeight(chatData.fontWeight);
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground(chatData.pageBackground);
        theme.setPageText(chatData.pageText);
        theme.setResponseContainer(chatData.responseContainer);
        theme.setResponseText(chatData.responseText);
        theme.setUserContainer(chatData.userContainer);
        theme.setUserText(chatData.userText);

        formsMethods.setValue('defaultTheme', 'my-theme');
        formsMethods.setValue('brandColor', chatData.brandColor);
        formsMethods.setValue('inputBorderColor', chatData.inputBorderColor);
        formsMethods.setValue('font', chatData.font);
        formsMethods.setValue('fontWeight', chatData.fontWeight);
        formsMethods.setValue('pageBackground', chatData.pageBackground);
        formsMethods.setValue('pageText', chatData.pageText);
        formsMethods.setValue('responseContainer', chatData.responseContainer);
        formsMethods.setValue('responseText', chatData.responseText);
        formsMethods.setValue('userContainer', chatData.userContainer);
        formsMethods.setValue('userText', chatData.userText);
        break;
      default:
        theme.setDefaultTheme('blue-light');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto, sans-serif');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        formsMethods.setValue('defaultTheme', 'blue-light');
        formsMethods.setValue('brandColor', '#3650DE');
        formsMethods.setValue('inputBorderColor', '#EAECF0');
        formsMethods.setValue('font', 'Roboto');
        formsMethods.setValue('fontWeight', 'regular');
        formsMethods.setValue('pageBackground', '#FFF');
        formsMethods.setValue('pageText', '#475467');
        formsMethods.setValue('responseContainer', '#F2F4F7');
        formsMethods.setValue('responseText', '#101828');
        formsMethods.setValue('userContainer', '#F2F4F7');
        formsMethods.setValue('userText', '#475467');
    }
  }

  const defaultTheme = formsMethods.watch('defaultTheme');
  const brandColor = formsMethods.watch('brandColor');
  const inputBorderColor = formsMethods.watch('inputBorderColor');
  const font = formsMethods.watch('font');
  const fontWeight = formsMethods.watch('fontWeight');
  const pageBackground = formsMethods.watch('pageBackground');
  const pageText = formsMethods.watch('pageText');
  const responseContainer = formsMethods.watch('responseContainer');
  const responseText = formsMethods.watch('responseText');
  const userContainer = formsMethods.watch('userContainer');

  useEffect(() => {
    definesThemeProperties(defaultTheme);
  }, [defaultTheme]);

  useEffect(() => {
    theme.setBrandColor(brandColor !== null ? brandColor : '');
    theme.setInputBorderColor(
      inputBorderColor !== null ? inputBorderColor : '',
    );
    theme.setFont(font !== null ? font : '');
    theme.setFontWeight(fontWeight !== null ? fontWeight : '');
    theme.setPageBackground(pageBackground !== null ? pageBackground : '');
    theme.setPageText(pageText !== null ? pageText : '');
    theme.setResponseContainer(
      responseContainer !== null ? responseContainer : '',
    );
    theme.setResponseText(responseText !== null ? responseText : '');
    theme.setUserContainer(userContainer !== null ? userContainer : '');
  }, [
    brandColor,
    inputBorderColor,
    font,
    fontWeight,
    pageBackground,
    pageText,
    responseContainer,
    responseText,
    userContainer,
  ]);

  return (
    <FormProvider {...formsMethods}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <form
            id="theme"
            onSubmit={formsMethods.handleSubmit(handleSubmitForm)}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <Box display="flex" gap="16px">
              <MuiSelectControlled
                selectProps={{
                  name: 'defaultTheme',
                  label: 'Pick a default theme',
                  placeholder: 'Pick a default theme',
                }}
                disabled={isEditDesign}
              >
                <MenuItem value="black-light">Black Light Mode</MenuItem>
                <MenuItem value="blue-light">Blue Light Mode</MenuItem>
                <MenuItem value="purple-light">Purple Light Mode</MenuItem>
                <MenuItem value="orange-light">Orange Light Mode</MenuItem>
                <MenuItem value="blue-dark">Blue Dark Mode</MenuItem>
                <MenuItem value="orange-dark">Orange Dark Mode</MenuItem>
                <MenuItem value="green-dark">Green Dark Mode</MenuItem>
              </MuiSelectControlled>

              <Button variant="outlined" onClick={handleEditDesign}>
                {isEditDesign ? 'Cancel' : 'Or Design my own'}
              </Button>
            </Box>

            {isEditDesign && (
              <>
                <Box display="flex" flexDirection="column" gap="16px">
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#101828',
                    }}
                  >
                    Personalize your chat
                  </Typography>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap="16px"
                    marginTop="16px"
                  >
                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'brandColor',
                        label: 'Brand Color',
                        placeholder: '#hexcode',
                      }}
                    />

                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'inputBorderColor',
                        label: 'Input Field Border',
                        placeholder: '#hexcode',
                      }}
                    />
                  </Box>

                  <Box display="flex" gap="8px">
                    <MuiSelectControlled
                      selectProps={{
                        name: 'font',
                        label: 'Change Font',
                        placeholder: 'Change Font',
                      }}
                    >
                      {fontFamily.map(font => (
                        <MenuItem value={font}>{font}</MenuItem>
                      ))}
                    </MuiSelectControlled>

                    <MuiSelectControlled
                      selectProps={{
                        name: 'fontWeight',
                        label: 'Font Weight',
                      }}
                    >
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="regular">Regular</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                    </MuiSelectControlled>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap="16px">
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#101828',
                    }}
                  >
                    Page Options
                  </Typography>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap="16px"
                    marginTop="16px"
                  >
                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'pageBackground',
                        label: 'Page Background',
                        placeholder: '#hexcode',
                      }}
                    />

                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'pageText',
                        label: 'Page Text',
                        placeholder: '#hexcode',
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap="16px">
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#101828',
                    }}
                  >
                    Container Options
                  </Typography>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap="16px"
                    marginTop="16px"
                  >
                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'responseContainer',
                        label: 'Response Container',
                        placeholder: '#hexcode',
                      }}
                    />

                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'responseText',
                        label: 'Response Text',
                        placeholder: '#hexcode',
                      }}
                    />

                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'userContainer',
                        label: 'User Container',
                        placeholder: '#hexcode',
                      }}
                    />

                    <MuiInputColorControlled
                      inputColorProps={{
                        name: 'userText',
                        label: 'User Text',
                        placeholder: '#hexcode',
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </form>
        </Grid>
        <Grid item xs={12} md={4}>
          <PreviewChatbot />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          type="submit"
          form="theme"
          variant="contained"
          sx={{ width: '160px', marginTop: '8px' }}
        >
          Save changes
        </Button>
      </Box>
    </FormProvider>
  );
}
